import { Draggable, Droppable } from '@hello-pangea/dnd'
import type { CSSProperties } from 'react'
import { ChevronRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { TaskStatusIcon, taskStatusTone } from '@/frontend/features/Fde/projectTaskPresentation'
import ProjectTaskMenu from '@/frontend/features/Fde/ProjectTaskMenu'
import ProjectTaskRow from '@/frontend/features/Fde/ProjectTaskRow'
import type { TaskSurfaceProps } from '@/frontend/features/Fde/ProjectTaskCanvas'
import type { TaskListGroup } from '@/frontend/features/Fde/taskListTree'

interface Props extends TaskSurfaceProps {
  groups: TaskListGroup[]
  collapsed: Set<string>
  onToggle: (id: string) => void
}
export default function ProjectTaskList(props: Props) {
  return (
    <div className="h-full overflow-y-auto pb-4">
      {props.groups.map((group) => (
        <section key={group.id} aria-label={group.label} className="mb-4">
          <header className="sticky top-0 z-10 flex h-10 items-center gap-2 border-y border-border/60 bg-muted px-4 sm:px-6">
            {group.status && (
              <span className={taskStatusTone[group.status].split(' ')[0]}>
                <TaskStatusIcon status={group.status} className="size-3.5" />
              </span>
            )}
            <button
              type="button"
              aria-expanded={!props.collapsed.has(group.id)}
              onClick={() => props.onToggle(group.id)}
              className="flex items-center gap-2 text-xs font-medium"
            >
              <ChevronRight
                className={cn(
                  'size-3.5 transition-transform',
                  !props.collapsed.has(group.id) && 'rotate-90',
                )}
              />
              {group.label}
            </button>
            <span className="text-xs tabular-nums text-muted-foreground">{group.count}</span>
          </header>
          {!props.collapsed.has(group.id) && (
            <Droppable droppableId={group.id} isDropDisabled={!props.canManage || props.busy}>
              {(provided, snapshot) => (
                <div
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  className={cn(
                    'min-h-4 bg-background',
                    snapshot.isDraggingOver && 'bg-primary/5 ring-1 ring-inset ring-primary/15',
                  )}
                >
                  {group.rows.map(({ task, depth, context, hasChildren }, index) => (
                    <Draggable
                      key={task.id}
                      draggableId={task.id}
                      index={index}
                      isDragDisabled={context || !props.canManage || props.busy}
                      disableInteractiveElementBlocking
                    >
                      {(drag, state) => (
                        <ProjectTaskMenu
                          task={task}
                          canManage={props.canManage}
                          busy={props.busy}
                          onSelect={() => props.onSelect(task.id)}
                          onPatch={props.onPatch}
                          onCopy={props.onCopy}
                          copy={props.copy}
                        >
                          <div
                            ref={drag.innerRef}
                            {...drag.draggableProps}
                            style={drag.draggableProps.style as CSSProperties}
                            data-task-id={task.id}
                            data-task-depth={depth}
                            title={context ? props.copy.management.task_filter_context : undefined}
                            className={cn(
                              'flex items-center border-b border-border/50 bg-background',
                              state.isDragging && 'shadow-lg',
                              context && 'text-muted-foreground',
                            )}
                          >
                            {/* Fixed spacers keep nested rows aligned without dynamic inline CSS. */}
                            {Array.from({ length: Math.min(depth, 8) }, (_, level) => (
                              <span
                                key={level}
                                aria-hidden
                                className="w-4 shrink-0 self-stretch border-r border-border/50 sm:w-6"
                              />
                            ))}
                            {hasChildren ? (
                              <button
                                type="button"
                                aria-expanded={!props.collapsed.has(task.id)}
                                aria-label={(props.collapsed.has(task.id)
                                  ? props.copy.management.expand_subtasks
                                  : props.copy.management.collapse_subtasks
                                ).replace('%{task}', task.title)}
                                onClick={() => props.onToggle(task.id)}
                                className="ml-1 flex size-7 shrink-0 items-center justify-center rounded text-muted-foreground hover:bg-muted focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring"
                              >
                                <ChevronRight
                                  className={cn(
                                    'size-3.5 transition-transform',
                                    !props.collapsed.has(task.id) && 'rotate-90',
                                  )}
                                />
                              </button>
                            ) : (
                              <span aria-hidden className="ml-1 w-7 shrink-0" />
                            )}
                            <ProjectTaskRow
                              task={task}
                              overdue={props.overdueIds.has(task.id)}
                              onSelect={() => props.onSelect(task.id)}
                              copy={props.copy}
                              nested={depth > 0}
                              dragHandleProps={context ? null : drag.dragHandleProps}
                            />
                          </div>
                        </ProjectTaskMenu>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          )}
        </section>
      ))}
    </div>
  )
}
